import { Box, CircularProgress } from '@mui/material';
import * as Sentry from '@sentry/react';
import { PaymentElement, useElements, useStripe } from '@stripe/react-stripe-js';
import type { StripePaymentElementOptions } from '@stripe/stripe-js';
import { type PaymentData, setPaymentIntent } from 'api/payment';
import { useUpdatePaperlessOptIn } from 'api/submission';
import type { AxiosError } from 'axios';
import { BINARY_CONSTANTS } from 'common/constants';
import { displayToastMessage } from 'components/ToastMessage';
import { PrimaryButton } from 'components/lib/PrimaryButton';
import { useFormik } from 'formik';
import { withRailBasename } from 'helpers/rail';
import { isEmpty } from 'lodash-es';
import React, { type FC, useMemo, useState } from 'react';
import * as yup from 'yup';
import AutopayEnrollment from './AutopayEnrollment';
import ElectronicDeliveryCheckbox from './ElectronicDeliveryCheckbox';
import PaymentFrequency from './PaymentFrequency';
import TermsAndConditions from './TermsAndConditions';

const PAYMENT_CONFIRMATION_ERROR_MESSAGE =
  'We could not confirm your payment. Please refresh the page or contact support team.';

interface CheckoutFormProps {
  paymentData: PaymentData;
  handleSetCheckoutFormIsRendered: (isRendered: boolean) => void;
  submissionId: string;
  paperlessOptIn: boolean;
}

export interface IPaymentFormikValues {
  accept_terms_and_conditions: boolean;
  is_recurring: string;
  accept_electronic_delivery: boolean;
  authorize_autopay: boolean;
}

export const buildValidationSchema = (isAutopayRequired: boolean) =>
  yup.object().shape({
    accept_terms_and_conditions: yup
      .boolean()
      .oneOf([true], 'You must agree to the Terms and Conditions to proceed.')
      .required(),
    is_recurring: yup
      .string()
      .oneOf(
        isAutopayRequired ? [BINARY_CONSTANTS.YES] : [BINARY_CONSTANTS.YES, BINARY_CONSTANTS.NO],
      )
      .required(),
    authorize_autopay: isAutopayRequired
      ? yup.boolean().oneOf([true], 'You must authorize automatic payments to proceed.').required()
      : yup.boolean(),
  });

const CheckoutForm: FC<CheckoutFormProps> = ({
  paymentData,
  submissionId,
  paperlessOptIn,
  handleSetCheckoutFormIsRendered,
}) => {
  const stripe = useStripe();
  const elements = useElements();
  const [isLoading, setIsLoading] = useState(false);
  const { mutateAsync: updatePaperlessOptIn } = useUpdatePaperlessOptIn(submissionId);

  const envAppUrl = `${window.location.origin}${withRailBasename(`/submission/${submissionId}/payment`)}`;

  const isAutopayRequired = Boolean(paymentData?.is_autopay_required);

  const validationSchema = useMemo(
    () => buildValidationSchema(isAutopayRequired),
    [isAutopayRequired],
  );

  const formik = useFormik<IPaymentFormikValues>({
    initialValues: {
      accept_terms_and_conditions: false,
      is_recurring: isAutopayRequired ? BINARY_CONSTANTS.YES : '',
      accept_electronic_delivery: paperlessOptIn,
      authorize_autopay: false,
    },
    validationSchema,
    onSubmit: () => {},
  });

  const validateFormik = async () => {
    await formik.submitForm();

    const errors = await formik.validateForm();

    if (!isEmpty(errors)) {
      const errorFieldId = errors.accept_terms_and_conditions
        ? 'accept_terms_and_conditions'
        : errors.authorize_autopay
          ? 'authorize_autopay'
          : null;

      if (errorFieldId) {
        const el = document.getElementById(errorFieldId);

        (el?.parentElement ?? el)?.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
        });
      }

      await formik.setTouched(
        {
          ...formik.touched,
          accept_terms_and_conditions: true,
          is_recurring: true,
          authorize_autopay: true,
        },
        false,
      );

      return false;
    }

    return true;
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    const isValid = await validateFormik();

    if (!isValid) {
      return;
    }

    // Stripe.js has not yet loaded.
    // Make sure to disable form submission until Stripe.js has loaded.
    if (!stripe || !elements) return;

    let enrollmentRequest: { isFailed: boolean; status: number } = {
      isFailed: false,
      status: 200,
    };
    const isRecurringOptionSelected =
      isAutopayRequired || formik.values.is_recurring === BINARY_CONSTANTS.YES;

    setIsLoading(true);

    try {
      if (formik.values.accept_electronic_delivery !== paperlessOptIn) {
        try {
          await updatePaperlessOptIn({ paperlessOptIn: formik.values.accept_electronic_delivery });
        } catch (error) {
          const err = error as AxiosError;
          displayToastMessage('ERROR', err?.message);
        }
      }

      if (isRecurringOptionSelected) {
        try {
          await setPaymentIntent({
            paymentIntentId: paymentData.payment_intent_id,
            policyLocator: paymentData.policy_locator,
            submissionId,
          });
        } catch (error) {
          const err = error as AxiosError;
          if (err.response && err.isAxiosError) {
            enrollmentRequest = { isFailed: true, status: err.response.status };
          } else {
            enrollmentRequest = { isFailed: true, status: 500 };
          }
        }
      }

      if (paymentData) {
        try {
          const { error } = await stripe.confirmPayment({
            elements,
            confirmParams: {
              // The URL to redirect the user to after the payment is confirmed
              return_url: isRecurringOptionSelected
                ? `${envAppUrl}/?payment_completed=true&enrollment_failed=${enrollmentRequest.isFailed}&status=${enrollmentRequest.status}`
                : `${envAppUrl}/?payment_completed=true`,
              payment_method_data: {
                billing_details: {
                  email: paymentData?.customer_email ?? undefined,
                },
              },
            },
          });

          if (error) {
            displayToastMessage('ERROR', error?.message);
          }
        } catch (error) {
          // Stripe rejects if the Payment Element is unmounted; report it so Sentry still sees it.
          Sentry.captureException(error);
          displayToastMessage('ERROR', PAYMENT_CONFIRMATION_ERROR_MESSAGE);
        }
      }
    } finally {
      setIsLoading(false);
    }
  };

  const paymentElementOptions: StripePaymentElementOptions = {
    layout: { type: 'tabs', defaultCollapsed: false },
    fields: { billingDetails: { email: 'never' } },
    defaultValues: {
      billingDetails: {
        address: { country: 'US' },
      },
    },
  };

  const handleOnPaymentElementReady = () => {
    handleSetCheckoutFormIsRendered(true);
  };

  return (
    <Box component="form" sx={{ pb: 2 }} id="payment-form" onSubmit={handleSubmit}>
      <PaymentElement
        id="payment-element"
        options={paymentElementOptions}
        onReady={handleOnPaymentElementReady}
      />

      {isAutopayRequired ? (
        <AutopayEnrollment formik={formik} isLoading={isLoading} />
      ) : (
        <PaymentFrequency formik={formik} isLoading={isLoading} />
      )}

      <TermsAndConditions
        formik={formik}
        isLoading={isLoading}
        isAutopayRequired={isAutopayRequired}
      />

      <ElectronicDeliveryCheckbox
        formik={formik}
        isLoading={isLoading}
        isAutopayRequired={isAutopayRequired}
      />

      <Box sx={{ width: '100%' }}>
        <PrimaryButton
          sx={{
            mt: '.75rem',
          }}
          disabled={isLoading || !stripe || !elements}
          id="submit"
          type="submit"
          data-test="pay-button"
          text={isLoading ? <CircularProgress color="inherit" size={20} /> : 'Pay Now'}
        />
      </Box>
    </Box>
  );
};

export default CheckoutForm;
